import React, { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { errText } from "../biz/i18n";
import { Card, Empty, Sheet, btnMain, btnSoft } from "../biz/ui";
import { MediaUploader } from "../product-form/MediaUploader";
import { useLabT } from "./i18n";
import { TextIn, Check, StatusSelect, StatusPill, pair } from "./fields";
import { useLabActions, RowActions } from "./LabExperiments";
import { Collapse } from "./QuestionBuilder";

function ExperimentPicker({ experiments, selected, onToggle }) {
  const { t, lang } = useLabT();
  return (
    <div>
      <p className="text-[13px] text-[#6B645A] mb-2">{t("pick_experiments")}</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {experiments.map((e) => (
          <button key={e.id} type="button" onClick={() => onToggle(e.id)} data-testid={`lab-r-exp-${e.id}`}
            className={`text-start rounded-xl border px-3 py-3 text-sm transition-colors ${selected.includes(e.id) ? "border-[#9A7445] bg-[#F3ECE1]" : "border-[#E4DBCD] bg-white"}`}>
            <b dir="ltr">{e.code}</b> · {(lang === "ar" ? e.name_ar : e.name_en) || e.name_en || e.name_ar}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReleaseMoreOptions({ p, set }) {
  const { t } = useLabT();
  return (
    <Collapse title={t("more_options")} testid="lab-r-more">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <TextIn dir="ltr" label={t("code")} value={p.code} onChange={(v) => set("code", v.toUpperCase())} testid="lab-r-code" />
          <StatusSelect t={t} value={p.status} onChange={(v) => set("status", v)} testid="lab-r-status" />
        </div>
        {pair(t, p, set, "title")}
        {pair(t, p, set, "description", { area: true })}
        <div className="grid grid-cols-3 gap-3">
          <TextIn type="number" label={t("discount_pct")} value={p.discount_pct} onChange={(v) => set("discount_pct", v)} testid="lab-r-discount" />
          <TextIn type="number" label={t("max_per_order")} value={p.max_per_order} onChange={(v) => set("max_per_order", v)} testid="lab-r-max" />
          <TextIn type="date" label={t("release_date")} value={p.release_date} onChange={(v) => set("release_date", v)} testid="lab-r-date" />
        </div>
        <div className="bg-[#1C1A17] rounded-xl p-3"><MediaUploader items={p.images || []} onChange={(v) => set("images", v)} accept="image/*" box="w-16 h-20" testId="lab-r-upload" okText={t("saved")} failText="Upload failed" /></div>
        <Check label={t("allow_individual")} checked={p.allow_individual} onChange={(v) => set("allow_individual", v)} testid="lab-r-individual" />
        <Check label={t("public")} checked={p.public} onChange={(v) => set("public", v)} testid="lab-r-public" />
      </div>
    </Collapse>
  );
}

function ReleaseActions({ isEdit, busy, save, onClose }) {
  const { t } = useLabT();
  return (
    <div className="flex flex-wrap gap-3 justify-end pt-2">
      <button onClick={onClose} className={btnSoft} data-testid="lab-rel-cancel">{t("cancel")}</button>
      {isEdit ? <button onClick={() => save()} disabled={busy} className={btnMain} data-testid="lab-rel-save">{t("save")}</button> : <>
        <button onClick={() => save("draft")} disabled={busy} className={btnSoft} data-testid="lab-rel-draft">{t("save_draft")}</button>
        <button onClick={() => save("available")} disabled={busy} className={btnMain} data-testid="lab-rel-publish">{t("publish")}</button></>}
    </div>
  );
}

function ReleaseForm({ initial, data, onClose, onSaved }) {
  const { t, lang } = useLabT();
  const nk = lang === "ar" ? "ar" : "en";
  const [p, setP] = useState(() => ({ status: "draft", public: true, experiment_ids: [], set_price: 0, sets_stock: 2, discount_pct: 0, max_per_order: 1, images: [], code: data.next_release_code, ...initial }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const toggleExp = (id) => set("experiment_ids", p.experiment_ids.includes(id) ? p.experiment_ids.filter((x) => x !== id) : [...p.experiment_ids, id]);
  const save = async (status) => {
    setBusy(true);
    try {
      const body = { ...p, status: status || p.status, public: status === "available" ? true : p.public };
      if (p.id) await api.put(`/admin/lab/releases/${p.id}`, body);
      else await api.post("/admin/lab/releases", body);
      toast.success(t("saved"));
      onSaved();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title={p.id ? `${t("edit")} · ${p.code}` : t("add_release")} onClose={onClose} testid="lab-rel-form">
      <div className="space-y-4">
        <TextIn dir={nk === "ar" ? "rtl" : "ltr"} label={t("release_name")} value={p[`title_${nk}`]} onChange={(v) => set(`title_${nk}`, v)} testid="lab-r-title" />
        <ExperimentPicker experiments={data.experiments} selected={p.experiment_ids} onToggle={toggleExp} />
        <div className="grid grid-cols-2 gap-3">
          <TextIn type="number" label={t("set_price")} value={p.set_price} onChange={(v) => set("set_price", v)} testid="lab-r-price" />
          <TextIn type="number" label={t("quantity")} value={p.sets_stock} onChange={(v) => set("sets_stock", v)} testid="lab-r-stock" />
        </div>
        <ReleaseMoreOptions p={p} set={set} />
        <ReleaseActions isEdit={!!p.id} busy={busy} save={save} onClose={onClose} />
      </div>
    </Sheet>
  );
}

export function LabReleases({ data, reload }) {
  const { t, lang, aed } = useLabT();
  const [edit, setEdit] = useState(null);
  const act = useLabActions("releases", reload);
  const codes = Object.fromEntries(data.experiments.map((e) => [e.id, e.code]));
  return (
    <div className="space-y-4">
      <button onClick={() => setEdit({})} className={btnMain} data-testid="lab-add-release"><Plus size={16} />{t("add_release")}</button>
      {!data.releases.length && <Empty>{t("no_items")}</Empty>}
      {data.releases.map((x) => (
        <Card key={x.id} testid={`lab-rel-${x.id}`}>
          <div className="flex items-center gap-2 flex-wrap"><b dir="ltr">{x.code}</b><StatusPill t={t} status={x.status} />
            <span className="text-xs text-[#8A8173]">{x.public ? t("visible") : t("hidden")}</span></div>
          <p className="mt-1">{(lang === "ar" ? x.title_ar : x.title_en) || x.title_en || x.title_ar}</p>
          <p className="text-xs text-[#8A8173] mt-1" dir="ltr">{x.experiment_ids.map((i) => codes[i] || "?").join(" · ")} — {aed(x.set_price)}{x.discount_pct ? ` −${x.discount_pct}%` : ""} · {x.sets_sold} {t("sold")}</p>
          <RowActions x={{ ...x, evaluations_count: 0 }} t={t} act={act} onEdit={() => setEdit(x)} stockKey="sets_stock" prefix="lab-rel" />
        </Card>
      ))}
      {edit && <ReleaseForm initial={edit} data={data} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
